// src/components/shared/CollapsibleFormSection.jsx
import React from 'react';

/**
 * Componente per sezioni collassabili del form
 * Permette di espandere/collassare sezioni per migliorare l'usabilità mobile
 */
export const CollapsibleFormSection = ({ 
  id, 
  title, 
  icon = '📝', 
  children, 
  isCollapsed = false, 
  onToggle,
  className = '',
  priority = 'normal',
  sectionKey // Alias per id (per compatibilità)
}) => {
  const sectionId = id || sectionKey;
  
  const handleToggle = (e) => {
    e.preventDefault();
    if (onToggle) {
      onToggle(sectionId);
    }
  };

  const sectionClasses = [
    'form-section',
    isCollapsed ? 'collapsed' : '',
    priority === 'high' ? 'priority-high' : '',
    priority === 'low' ? 'priority-low' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={sectionClasses} data-section-id={sectionId}>
      <h4 
        onClick={handleToggle} 
        role="button" 
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle(e);
          }
        }}
        aria-expanded={!isCollapsed}
        aria-controls={`section-content-${sectionId}`}
        id={`section-header-${sectionId}`}
      >
        <span>{icon} {title}</span>
        <span className="section-toggle-icon">
          {isCollapsed ? '▶' : '▼'}
        </span>
      </h4>
      <div 
        id={`section-content-${sectionId}`}
        className="section-content"
        role="region"
        aria-labelledby={`section-header-${sectionId}`}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Componente per controlli delle sezioni (Espandi/Collassa tutto)
 * Visibile solo su mobile (max-width: 768px)
 */
export const SectionControls = ({ 
  onExpandAll, 
  onCollapseAll, 
  totalSections = 0, 
  collapsedCount = 0 
}) => {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    
    // Chiama subito per impostare lo stato iniziale
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Non mostrare su desktop
  if (!isMobile) return null;
  
  const openCount = totalSections - collapsedCount;
  
  return (
    <div className="section-controls-mobile">
      <div className="controls-info">
        {openCount > 0 ? (
          <span>{openCount} di {totalSections} sezioni aperte</span>
        ) : (
          <span>Tutte le sezioni chiuse</span>
        )}
      </div>
      <div className="controls-actions">
        <button 
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={onExpandAll}
          aria-label="Espandi tutte le sezioni"
        >
          📖 Espandi Tutto
        </button>
        <button 
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onCollapseAll}
          aria-label="Chiudi tutte le sezioni"
        >
          📚 Chiudi Tutto
        </button>
      </div>
    </div>
  );
};

// Export di default per maggiore compatibilità
export default CollapsibleFormSection;